import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Calendar, Clock, MapPin, Video, Phone, Users, Edit, Trash2, Star, CheckCircle } from 'lucide-react';
import { format, parseISO, addDays } from 'date-fns';
import InterviewEvaluationModal from './InterviewEvaluationModal';

interface JobApplication {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  status: string;
  job_posting_id: string;
}

interface InterviewSchedule {
  id: string;
  job_application_id: string;
  business_id: string;
  interviewer_user_id?: string;
  interview_type: string;
  scheduled_at: string;
  duration_minutes: number;
  location?: string;
  meeting_link?: string;
  notes?: string;
  status: string;
  evaluation_notes?: string;
  technical_score?: number;
  communication_score?: number;
  culture_fit_score?: number;
  overall_recommendation?: string;
  created_at: string;
}

interface InterviewSchedulerProps {
  isOpen: boolean;
  onClose: () => void;
  application: JobApplication | null;
  businessId: string;
  onInterviewScheduled?: () => void;
}

const InterviewScheduler: React.FC<InterviewSchedulerProps> = ({
  isOpen,
  onClose,
  application,
  businessId,
  onInterviewScheduled
}) => {
  const [existingInterviews, setExistingInterviews] = useState<InterviewSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingInterview, setEditingInterview] = useState<InterviewSchedule | null>(null);
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [selectedInterviewForEvaluation, setSelectedInterviewForEvaluation] = useState<InterviewSchedule | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    interview_type: 'in-person',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    location: '',
    meeting_link: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen && application) {
      fetchExistingInterviews();
      resetForm();
    }
  }, [isOpen, application]);

  const fetchExistingInterviews = async () => {
    if (!application) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('interview_schedules')
        .select('*')
        .eq('job_application_id', application.id)
        .order('scheduled_at', { ascending: true });

      if (error) throw error;
      setExistingInterviews(data || []);
    } catch (error: unknown) {
      console.error('Error fetching interviews:', error);
      toast.error('Failed to load existing interviews');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      interview_type: 'in-person',
      scheduled_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      scheduled_time: '10:00',
      duration_minutes: 60,
      location: '',
      meeting_link: '',
      notes: ''
    });
    setEditingInterview(null);
  };

  const handleEditInterview = (interview: InterviewSchedule) => {
    const scheduledDate = parseISO(interview.scheduled_at);
    setFormData({
      interview_type: interview.interview_type,
      scheduled_date: format(scheduledDate, 'yyyy-MM-dd'),
      scheduled_time: format(scheduledDate, 'HH:mm'),
      duration_minutes: interview.duration_minutes || 60,
      location: interview.location || '',
      meeting_link: interview.meeting_link || '',
      notes: interview.notes || ''
    });
    setEditingInterview(interview);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application) return;

    // Validation
    if (!formData.scheduled_date || !formData.scheduled_time) {
      toast.error('Please select a date and time for the interview');
      return;
    }

    if (formData.interview_type === 'in-person' && !formData.location.trim()) {
      toast.error('Please provide a location for in-person interviews');
      return;
    }

    if (formData.interview_type === 'video' && !formData.meeting_link.trim()) {
      toast.error('Please provide a meeting link for video interviews');
      return;
    }

    setSaving(true);

    try {
      const scheduledAt = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`).toISOString();

      const interviewData = {
        job_application_id: application.id,
        business_id: businessId,
        interview_type: formData.interview_type,
        scheduled_at: scheduledAt,
        duration_minutes: formData.duration_minutes,
        location: formData.location.trim() || null,
        meeting_link: formData.meeting_link.trim() || null,
        notes: formData.notes.trim() || null,
        status: 'scheduled'
      };

      if (editingInterview) {
        // Update existing interview
        const { error } = await supabase
          .from('interview_schedules')
          .update(interviewData)
          .eq('id', editingInterview.id);

        if (error) throw error;
        toast.success('Interview updated successfully');
      } else {
        // Create new interview
        const { error } = await supabase
          .from('interview_schedules')
          .insert(interviewData);

        if (error) throw error;

        // Update application status to 'interview-scheduled'
        await supabase
          .from('job_applications')
          .update({ status: 'interview-scheduled' })
          .eq('id', application.id);

        toast.success('Interview scheduled successfully');
      }

      fetchExistingInterviews();
      resetForm();
      onInterviewScheduled?.();
    } catch (error: unknown) {
      console.error('Error saving interview:', error);
      toast.error('Failed to save interview');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInterview = async (interviewId: string) => {
    if (!confirm('Are you sure you want to delete this interview?')) return;

    try {
      const { error } = await supabase
        .from('interview_schedules')
        .delete()
        .eq('id', interviewId);

      if (error) throw error;

      toast.success('Interview deleted successfully');
      fetchExistingInterviews();
      
      // If no more interviews, update application status back to 'screening'
      const remainingInterviews = existingInterviews.filter(i => i.id !== interviewId);
      if (remainingInterviews.length === 0 && application) {
        await supabase
          .from('job_applications')
          .update({ status: 'screening' })
          .eq('id', application.id);
      }
    } catch (error: unknown) {
      console.error('Error deleting interview:', error);
      toast.error('Failed to delete interview');
    }
  };

  const handleEvaluateInterview = (interview: InterviewSchedule) => {
    setSelectedInterviewForEvaluation(interview);
    setEvaluationModalOpen(true);
  };

  const markInterviewCompleted = async (interviewId: string) => {
    try {
      const { error } = await supabase
        .from('interview_schedules')
        .update({ status: 'completed' })
        .eq('id', interviewId);

      if (error) throw error;

      toast.success('Interview marked as completed');
      fetchExistingInterviews();
    } catch (error: unknown) {
      console.error('Error updating interview status:', error);
      toast.error('Failed to update interview status');
    }
  };

  const getInterviewTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'phone': return <Phone className="h-4 w-4" />;
      case 'panel': return <Users className="h-4 w-4" />;
      default: return <MapPin className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'scheduled': { variant: 'default' as const, label: 'Scheduled' },
      'confirmed': { variant: 'default' as const, label: 'Confirmed' },
      'completed': { variant: 'secondary' as const, label: 'Completed' },
      'cancelled': { variant: 'destructive' as const, label: 'Cancelled' },
      'no-show': { variant: 'destructive' as const, label: 'No Show' }
    };
    
    const config = statusMap[status as keyof typeof statusMap] || statusMap.scheduled;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Interview Scheduler
          </DialogTitle>
          <DialogDescription>
            Schedule and manage interviews for {application?.first_name} {application?.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-6 h-[70vh]">
          {/* Existing Interviews */}
          <div className="w-1/2 space-y-4">
            <h3 className="text-lg font-semibold">Existing Interviews</h3>
            
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : existingInterviews.length > 0 ? (
              <div className="space-y-3 overflow-y-auto max-h-[55vh]">
                {existingInterviews.map((interview) => (
                  <Card key={interview.id}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getInterviewTypeIcon(interview.interview_type)}
                          <span className="font-medium capitalize">
                            {interview.interview_type.replace('-', ' ')} Interview
                          </span>
                        </div>
                        {getStatusBadge(interview.status)}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>{format(parseISO(interview.scheduled_at), 'PPP')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {format(parseISO(interview.scheduled_at), 'p')} 
                            ({interview.duration_minutes} min)
                          </span>
                        </div>
                        {interview.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">{interview.location}</span>
                          </div>
                        )}
                        {interview.meeting_link && (
                          <div className="flex items-center gap-2">
                            <Video className="h-4 w-4 text-muted-foreground" />
                            <a 
                              href={interview.meeting_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-sm"
                            >
                              Join Meeting
                            </a>
                          </div>
                        )}
                        {interview.notes && (
                          <p className="text-muted-foreground text-xs bg-muted p-2 rounded">
                            {interview.notes}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {interview.status === 'scheduled' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditInterview(interview)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => markInterviewCompleted(interview.id)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Mark Complete
                            </Button>
                          </>
                        )}
                        
                        {interview.status === 'completed' && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleEvaluateInterview(interview)}
                          >
                            <Star className="h-4 w-4 mr-1" />
                            {interview.evaluation_notes ? 'Edit Evaluation' : 'Evaluate'}
                          </Button>
                        )}
                        
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteInterview(interview.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center">
                    No interviews scheduled yet
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Schedule New/Edit Interview Form */}
          <div className="w-1/2 border-l pl-6">
            <h3 className="text-lg font-semibold mb-4">
              {editingInterview ? 'Edit Interview' : 'Schedule New Interview'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto max-h-[55vh]">
              <div>
                <Label htmlFor="interview_type">Interview Type</Label>
                <Select 
                  value={formData.interview_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, interview_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in-person">In-Person</SelectItem>
                    <SelectItem value="video">Video Call</SelectItem>
                    <SelectItem value="phone">Phone Call</SelectItem>
                    <SelectItem value="panel">Panel Interview</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="scheduled_date">Date</Label>
                  <Input
                    id="scheduled_date"
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="scheduled_time">Time</Label>
                  <Input
                    id="scheduled_time"
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="duration_minutes">Duration (minutes)</Label>
                <Select 
                  value={formData.duration_minutes.toString()} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.interview_type === 'in-person' && (
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Office address, conference room, etc."
                    required
                  />
                </div>
              )}

              {(formData.interview_type === 'video' || formData.interview_type === 'panel') && (
                <div>
                  <Label htmlFor="meeting_link">Meeting Link</Label>
                  <Input
                    id="meeting_link"
                    type="url"
                    value={formData.meeting_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, meeting_link: e.target.value }))}
                    placeholder="https://zoom.us/j/..."
                    required
                  />
                </div>
              )}

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Special instructions, preparation notes, etc."
                  rows={3}
                />
              </div>
            </form>
          </div>
        </div>

        <DialogFooter className="pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {editingInterview && (
            <Button variant="outline" onClick={resetForm}>
              Cancel Edit
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : editingInterview ? 'Update Interview' : 'Schedule Interview'}
          </Button>
        </DialogFooter>

        {/* Interview Evaluation Modal */}
        <InterviewEvaluationModal
          isOpen={evaluationModalOpen}
          onClose={() => {
            setEvaluationModalOpen(false);
            setSelectedInterviewForEvaluation(null);
          }}
          interview={selectedInterviewForEvaluation}
          application={application}
          onEvaluationSaved={() => {
            fetchExistingInterviews();
            onInterviewScheduled?.();
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default InterviewScheduler;